import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Swords, Trophy, AlertTriangle, RotateCcw, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

export const NotificationBell = ({ testIdSuffix = "" }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = () => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.items || []);
      setUnread(data.unread || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user || user === false) return;
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [user]); // eslint-disable-line

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user || user === false) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      try { await api.post("/notifications/read"); } catch (e) { console.error("Failed to mark notifications read", e); }
    }
  };

  const textFor = (n) => {
    if (n.type === "debate_proposed") return t("notif_proposed", { a: n.actor_name, t: n.topic });
    if (n.type === "refund_warning") return t("notif_refund_warning", { n: n.result || "2" });
    if (n.type === "debate_refunded") return t("notif_debate_refunded", { t: n.topic });
    if (n.type === "participation_confirm") return t("notif_participation_confirm", { t: n.topic });
    if (n.type === "dispute_rejected") return t("notif_dispute_rejected", { t: n.topic });
    const base = t("notif_resolved", { t: n.topic });
    const suffix = n.result === "win" ? ` — ${t("notif_win")}` : n.result === "tie" ? ` — ${t("notif_tie")}` : "";
    return base + suffix;
  };

  const iconFor = (n) => {
    if (n.type === "debate_proposed") return <Swords size={16} className="text-emerald-400" />;
    if (n.type === "refund_warning" || n.type === "dispute_rejected") return <AlertTriangle size={16} className="text-red-400" />;
    if (n.type === "debate_refunded") return <RotateCcw size={16} className="text-red-400" />;
    if (n.type === "participation_confirm") return <CheckCircle2 size={16} className="text-sky-400" />;
    return <Trophy size={16} className="text-amber-400" />;
  };

  return (
    <div ref={wrapRef} className="relative">
      <button data-testid={`notif-bell-btn${testIdSuffix}`} onClick={toggle} aria-label={t("notif_title")}
        className="relative text-zinc-400 hover:text-white transition-colors p-1.5">
        <Bell size={19} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-zinc-950 text-[10px] font-black flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="notif-dropdown"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl shadow-black/60 overflow-hidden z-[70]">
            <div className="px-4 py-3 border-b border-zinc-800">
              <p className="text-sm font-bold text-white">{t("notif_title")}</p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-6 text-sm text-zinc-500 text-center">{t("notif_empty")}</p>
              ) : (
                items.map((n) => (
                  <button key={n.id} data-testid="notif-item"
                    onClick={() => { setOpen(false); navigate(["refund_warning", "participation_confirm", "dispute_rejected"].includes(n.type) ? "/dashboard" : "/debates"); }}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors ${n.read ? "" : "bg-emerald-950/20"}`}>
                    <span className="mt-0.5 shrink-0">
                      {iconFor(n)}
                    </span>
                    <span className="text-xs text-zinc-300 leading-relaxed">{textFor(n)}</span>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
